import { Injectable } from '@angular/core';
import {
  AdditionalBusinessInfoForm, BankInformationForm,
  DBAForm,
  LegalForm,
  NuveiApplicationForm, NuveiApplicationFormUpdate, SalesProfileForm
} from "../models/nuvei-application.model";
import { SupabaseService } from "./supabase.service";
import { camelCaseToSnake, trimAllStrings } from "../../../../common/src/lib/services";
import { BusinessOwnersFormErrors, FormErrors } from "../helpers/nuvei/nuvei-form-validation";
import moment from "moment";

type Nullable<T> = {
  [K in keyof T]: T[K] | null;
};

export interface NuveiDocumentFileData {
  downloadUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  timestamp: Date;
}

export type SavedNuveiApplicationForm = Nullable<NuveiApplicationForm> & {
  status: 'created' | 'pending'
  documents: {
    proofOfBusiness?: NuveiDocumentFileData,
    voidCheck?: NuveiDocumentFileData,
    previousPaymentProcessor?: NuveiDocumentFileData,
  },
  formDocument?: {
    documentUrl: string,
    documentApproval?: string,
    documentExpiration?: Date,
  }
  signatures: {
    owner?: {
      downloadUrl: string,
      signerName: string,
      timestamp: Date,
      submitted: boolean,
      approval: string | null
    },
    guarantor?: {
      downloadUrl: string,
      signerName: string,
      timestamp: Date,
      submitted: boolean,
      approval: string | null
    },
  }
}

export type SubmitApplicationErrors = {
  legalForm: null | FormErrors<LegalForm>,
  dbaForm: null | FormErrors<DBAForm>,
  additionalBusinessInfoForm: null | FormErrors<AdditionalBusinessInfoForm>,
  salesProfileForm: null | FormErrors<SalesProfileForm>,
  businessOwnersForm: null | BusinessOwnersFormErrors,
  bankInformationForm: null | FormErrors<BankInformationForm>,
  files: null | {
    proofOfBusiness: string,
    voidCheck: string,
    previousPaymentProcessor: string,
  }
}

export function fixDates(data: any): any {
  if (typeof data === 'object' && data !== null) {
    if (moment.isMoment(data) || data instanceof Date) {
      return moment(data).format('YYYY-MM-DD') + 'T00:00:00.000Z';
    }
    if (Array.isArray(data)) {
      return data.map(d => fixDates(d));
    } else {
      return Object.fromEntries(
          Object.entries(data).map(([key, value]) => [key, fixDates(value)])
      );
    }
  }
  return data;
}

@Injectable({
  providedIn: 'root'
})
export class NuveiService {

  constructor(
      private supabaseService: SupabaseService,
  ) { }

  getApplicationForm(businessId: string, token?: string) {
    return this.supabaseService.edgeFunc<SavedNuveiApplicationForm>('get-application-form', { businessId, token });
  }

  async updateForm(data: NuveiApplicationFormUpdate) {
    // Trim all string fields before processing dates and sending to server
    const trimmedData = trimAllStrings(data);
    const res = await this.supabaseService.supabase.schema('nuvei').rpc('update_application_form', fixDates(trimmedData));
    if(res.error)
      console.log(res.error);
  }

  async uploadSignature(businessId: string, file: Blob, approval: string, token?: string) {
    const formData = new FormData();
    formData.append("signature", file);
    formData.append("businessId", businessId);
    formData.append("approval", approval);
    if(token)
      formData.append("token", token);
    const { data, error } = await this.supabaseService.supabase.functions.invoke<void>('upload-signature', { body: formData });
    if (error)
      throw error;
  }

  async finalizeSignature(businessId: string, token?: string) {
    const result = await this.supabaseService.edgeFunc<{ downloadUrl: string }>('finalize-signature', { businessId, token });
    return result.downloadUrl;
  }

  uploadFile(businessId: string, documentType: string, file: Blob) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("businessId", businessId);
    formData.append("documentType", camelCaseToSnake(documentType));
    return this.supabaseService.supabase.functions.invoke('upload-application-file', { body: formData });
  }

  deleteFile(businessId: string, documentType: string) {
    return this.supabaseService.edgeFunc('delete-application-file', { businessId, documentType: camelCaseToSnake(documentType) });
  }

  submitApplication(businessId: string, approval: string) {
    return this.supabaseService.edgeFunc<void>('submit-application', { businessId, approval });
  }

  requestApproval(businessId: string, type: 'signature' | 'document', token?: string) {
    return this.supabaseService.edgeFunc<string>('request-approval', { businessId, type, token });
  }

  revokeApproval(businessId: string, approval: string, type: 'signature' | 'document', token?: string) {
    return this.supabaseService.edgeFunc<string | null>('revoke-approval', {
      businessId, approval, type, token
    });
  }

  addEvent(businessId: string, event: string, token?: string) {
    return this.supabaseService.edgeFunc<void>('add-event', {
      businessId, event, token
    });
  }

}
